import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, BadgeCheck, X, Award, FileText, Maximize2 } from 'lucide-react';
import { CERTIFICATES } from '../data/mock';
import { Reveal, SplitText, Magnetic, AnimatedDivider, useParallax } from '../components/effects';
import CTASection from '../components/CTASection';

// ------------------------------------------------------------------
// HERO BLOCK
// ------------------------------------------------------------------
const HeroBlock = () => {
  const imgRef = useParallax(0.06);

  return (
    <section className="relative pt-[170px] lg:pt-[200px] pb-24 lg:pb-32 overflow-hidden bg-[#F5F3EF]">
      {/* Background subtle texture */}
      <div className="absolute inset-0 opacity-[0.18] pointer-events-none">
        <img
          ref={imgRef}
          src="/site/tarhun-bina-hero.webp"
          alt=""
          className="w-full h-full object-cover will-change-transform"
          style={{ transform: 'scale(1.12)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(245,243,239,0.85) 0%, rgba(245,243,239,0.45) 45%, rgba(245,243,239,0.96) 100%)',
          }}
        />
      </div>

      {/* Giant background "BELGELER" wordmark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-4 lg:-top-10 left-0 right-0 select-none"
      >
        <div
          className="font-display text-center text-[26vw] lg:text-[22vw] leading-[0.78] tracking-[-0.04em] font-medium whitespace-nowrap"
          style={{ color: '#72272D', opacity: 0.045 }}
        >
          BELGELER
        </div>
      </div>

      <div className="container-x relative">
        <div className="flex items-center gap-3 mb-7">
          <AnimatedDivider width="3rem" />
          <Reveal delay={100} y={8} duration={700}>
            <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
              Kurumsal Belgeler · Tarhun Güvencesi
            </span>
          </Reveal>
        </div>

        <h1
          className="font-display text-[44px] sm:text-[64px] lg:text-[92px] leading-[0.98] tracking-[-0.022em] text-[#1A1A1A] max-w-5xl"
          style={{ fontWeight: 500 }}
        >
          <SplitText text="Güvenin," delay={120} />
          <br />
          <span className="italic font-light text-[#72272D]">
            <SplitText text="yazılı kanıtı." delay={520} />
          </span>
        </h1>

        <Reveal delay={900}>
          <p className="mt-9 text-[16px] lg:text-[18px] text-[#1A1A1A]/70 max-w-2xl leading-[1.7]">
            Kalite, gıda güvenliği, çevre ve iş sağlığı — 14 yıllık operasyonumuzun her halkası
            <strong className="font-medium text-[#1A1A1A]"> bağımsız akredite kuruluşlar</strong> ve
            <strong className="font-medium text-[#1A1A1A]"> T.C. resmi kurumlar</strong> tarafından
            belgelendirilmiştir. Şeffaflık, bizim için bir tercih değil — bir alışkanlıktır.
          </p>
        </Reveal>

        {/* Mini stats — credibility numbers */}
        <Reveal delay={1100}>
          <div className="mt-14 lg:mt-16 grid grid-cols-3 gap-8 lg:gap-16 max-w-2xl">
            {[
              { v: '06', l: 'Aktif Belge' },
              { v: '14', l: 'Yıl Belgeli Üretim' },
              { v: '3 Yıl', l: 'Belgelendirme Periyodu' },
            ].map((s) => (
              <div key={s.l} className="border-t border-[#9A7F4F]/35 pt-4">
                <div className="font-display numeric text-[#1A1A1A] text-[28px] lg:text-[40px] leading-none tracking-[-0.02em] font-medium">
                  {s.v}
                </div>
                <div className="mt-3 text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/60 font-medium">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ------------------------------------------------------------------
// CERTIFICATE GRID — editorial cards (clickable -> modal lightbox)
// ------------------------------------------------------------------
const CertificateCard = ({ cert, onOpen }) => {
  return (
    <Reveal y={36} duration={900}>
      <article
        onClick={() => onOpen(cert)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(cert); }}
        role="button"
        tabIndex={0}
        data-testid={`certificate-card-${cert.id}`}
        className="group relative h-full bg-[#FBF9F4] border border-[#9A7F4F]/15 rounded-sm overflow-hidden cursor-pointer transition-all duration-500 hover:border-[#72272D]/45 hover:shadow-[0_36px_70px_-30px_rgba(114,39,45,0.32)]"
      >
        {/* Top — certificate thumbnail */}
        <div className="relative aspect-[4/3] overflow-hidden bg-[#F5F3EF]">
          <img
            src={cert.image}
            alt={cert.title}
            className="absolute inset-0 w-full h-full object-contain p-8 transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
            loading="lazy"
          />

          {/* Subtle vignette gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C0B]/[0.07] via-transparent to-transparent" />

          {/* Top-left number */}
          <div className="absolute top-5 left-5 flex items-center gap-2.5">
            <span className="font-display numeric text-[#72272D] text-[26px] leading-none tracking-[-0.02em] font-medium">
              {cert.indexLabel}
            </span>
            <span className="h-px w-8 bg-[#C9A875]" />
          </div>

          {/* Top-right "view" affordance */}
          <div className="absolute top-5 right-5 h-9 w-9 grid place-items-center rounded-sm bg-[#0A0C0B]/85 text-[#C9A875] backdrop-blur-sm opacity-0 translate-y-[-4px] group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
            <Maximize2 size={15} strokeWidth={1.6} />
          </div>

          {/* Bottom-right category badge */}
          <div className="absolute bottom-5 right-5">
            <span className="inline-flex items-center gap-2 bg-[#F5F3EF]/95 backdrop-blur-sm text-[#1A1A1A] px-3 py-1.5 text-[9.5px] tracking-[0.28em] uppercase font-medium rounded-sm border border-[#9A7F4F]/25">
              <BadgeCheck size={11} className="text-[#9A7F4F]" strokeWidth={1.7} />
              {cert.category}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-7 lg:p-8">
          <h3
            className="font-display text-[24px] lg:text-[28px] text-[#1A1A1A] leading-[1.1] tracking-[-0.01em]"
            style={{ fontWeight: 500 }}
          >
            {cert.title}
          </h3>
          <div className="mt-2 text-[13px] italic font-light text-[#72272D]">
            {cert.subtitle}
          </div>

          <p className="mt-5 text-[13.5px] text-[#1A1A1A]/68 leading-[1.7]">
            {cert.description}
          </p>

          {/* Meta rows */}
          <div className="mt-6 pt-5 border-t border-[#9A7F4F]/25 space-y-2.5">
            <Meta label="Belge No" value={cert.certNo} />
            <Meta label="İlk Veriliş" value={cert.initialDate} />
            <Meta label="Geçerlilik" value={cert.validUntil} />
            <Meta label="Akreditasyon" value={cert.accreditation} />
          </div>

          {/* CTA hint */}
          <div className="mt-7 flex items-center gap-2 text-[11.5px] tracking-[0.22em] uppercase text-[#9A7F4F] font-medium opacity-70 group-hover:opacity-100 transition-opacity">
            Belgeyi Görüntüle
            <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
          </div>
        </div>

        {/* Bottom animated hairline */}
        <div className="absolute bottom-0 left-0 h-px bg-[#72272D] w-0 group-hover:w-full transition-all duration-700" />
      </article>
    </Reveal>
  );
};

const Meta = ({ label, value }) => (
  <div className="grid grid-cols-12 gap-2 sm:gap-3 text-[10.5px] sm:text-[11.5px] leading-[1.5]">
    <div className="col-span-5 sm:col-span-5 tracking-[0.18em] sm:tracking-[0.24em] uppercase text-[#1A1A1A]/55 font-medium">
      {label}
    </div>
    <div className="col-span-7 sm:col-span-7 text-[#1A1A1A]/85 numeric break-words">{value}</div>
  </div>
);

// ------------------------------------------------------------------
// LIGHTBOX MODAL — full-size certificate viewer
// ------------------------------------------------------------------
const CertificateLightbox = ({ cert, onClose }) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!cert) return;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [cert, handleKeyDown]);

  if (!cert) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 lg:p-10 backdrop-blur-md"
      style={{ background: 'rgba(10,12,11,0.92)' }}
      data-testid="certificate-lightbox"
    >
      {/* Close */}
      <button
        onClick={onClose}
        aria-label="Kapat"
        data-testid="lightbox-close"
        className="absolute top-5 right-5 lg:top-8 lg:right-8 z-10 h-11 w-11 grid place-items-center rounded-sm border border-[#C9A875]/40 text-[#F5F0E5] hover:bg-[#C9A875] hover:text-[#0A0C0B] hover:border-[#C9A875] transition-colors"
      >
        <X size={20} strokeWidth={1.6} />
      </button>

      {/* Header info top-left */}
      <div className="absolute top-5 left-5 lg:top-8 lg:left-12 max-w-md">
        <div className="text-[10.5px] tracking-[0.34em] uppercase text-[#C9A875] font-medium mb-2">
          {cert.category}
        </div>
        <div
          className="font-display text-[22px] lg:text-[28px] text-[#F5F0E5] leading-tight tracking-[-0.01em]"
          style={{ fontWeight: 500 }}
        >
          {cert.title}
        </div>
        <div className="mt-1 text-[12px] italic font-light text-[#C9A875]">
          {cert.subtitle}
        </div>
      </div>

      {/* Image */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-5xl w-full max-h-[88vh] flex items-center justify-center"
      >
        <img
          src={cert.image}
          alt={`${cert.title} — ${cert.subtitle}`}
          className="max-w-full max-h-[88vh] object-contain shadow-[0_50px_120px_-30px_rgba(0,0,0,0.6)]"
        />
      </div>

      {/* Bottom meta */}
      <div className="absolute bottom-5 left-5 right-5 lg:bottom-8 lg:left-12 lg:right-12 flex flex-wrap items-center justify-between gap-3 text-[#F5F0E5]/72">
        <div className="text-[11px] tracking-[0.24em] uppercase">
          {cert.issuer}
        </div>
        <div className="text-[11px] numeric tracking-[0.18em] text-[#C9A875]">
          {cert.certNo}  ·  {cert.validUntil}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// MAIN PAGE
// ------------------------------------------------------------------
const Sertifikalar = () => {
  const [openCert, setOpenCert] = useState(null);

  return (
    <>
      <HeroBlock />

      {/* GRID SECTION */}
      <section className="relative py-24 lg:py-36 bg-[#EFEBE3] overflow-hidden">
        {/* Dot grid texture */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #1A1A1A 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />

        <div className="container-x relative">
          {/* Section header */}
          <div className="max-w-3xl mb-14 lg:mb-20">
            <div className="flex items-center gap-3 mb-7">
              <AnimatedDivider width="3rem" />
              <Reveal delay={100} y={8} duration={700}>
                <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                  Resmi Belgelerimiz
                </span>
              </Reveal>
            </div>

            <Reveal delay={150}>
              <h2
                className="font-display text-[34px] lg:text-[52px] text-[#1A1A1A] leading-[1.06] tracking-[-0.02em]"
                style={{ fontWeight: 500 }}
              >
                Belgelendirilmiş bir <span className="italic font-light text-[#72272D]">disiplin</span>.
              </h2>
            </Reveal>

            <Reveal delay={350}>
              <p className="mt-7 text-[15px] lg:text-[16px] text-[#1A1A1A]/68 leading-[1.7] max-w-xl">
                Kartlara tıklayarak sertifikaların orijinal görsellerini büyütülmüş şekilde
                inceleyebilirsiniz. Tüm belgeler bağımsız akredite kuruluşlar veya T.C. resmi
                kurumlar tarafından düzenlenmiştir.
              </p>
            </Reveal>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-10">
            {CERTIFICATES.map((cert) => (
              <CertificateCard key={cert.id} cert={cert} onOpen={setOpenCert} />
            ))}
          </div>

          {/* Trust note */}
          <Reveal delay={300}>
            <div className="mt-20 lg:mt-28 max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center gap-3 mb-7">
                <span className="h-px w-12 bg-[#9A7F4F]" />
                <ShieldCheck size={16} className="text-[#9A7F4F]" strokeWidth={1.5} />
                <span className="h-px w-12 bg-[#9A7F4F]" />
              </div>
              <p className="font-display italic text-[#1A1A1A] text-[20px] lg:text-[26px] leading-[1.4] tracking-[-0.005em]">
                "Söz vermiyoruz. Protokol imzalıyoruz. Her belge — yıllık denetlenir,
                şeffaf olarak müşterilerimizle paylaşılır."
              </p>
              <Magnetic strength={0.18}>
                <Link
                  to="/iletisim"
                  className="group inline-flex items-center gap-3 mt-10 bg-[#72272D] text-[#F5F0E5] px-8 py-[18px] text-[11.5px] tracking-[0.24em] uppercase font-semibold rounded-sm transition-colors duration-300 hover:bg-[#5C1F25]"
                  data-testid="certificates-cta"
                >
                  Belgeleri Talep Edin
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Magnetic>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />

      {/* Lightbox */}
      <CertificateLightbox cert={openCert} onClose={() => setOpenCert(null)} />
    </>
  );
};

export default Sertifikalar;
